import type { ComponentType } from 'react'
import type {
  BaseNodeData,
  GroupTaskExecutionContext,
  NodeContextPayloadLike,
} from './node-registry'
import { readOpPreference } from './op-preference-storage'
import type { NodeToolbarHostComponentProps } from './node-toolbar-host-types'

// 文件说明：定义 OP 注册中心的数据结构和运行时查询能力。

export interface OpTargetDisplayHint {
  aspectRatio?: string
  width?: number
  height?: number
}

export interface OpMenuCreateConfig {
  nodeType: string
  toolbar?: string
  autoOpenEditor?: boolean
  createCustomData?: (args: {
    sourceNodeIds: string[]
    targetNodeType: string
  }) => Record<string, unknown>
}

export interface OpMenuIconProps {
  className?: string
}

export interface OpMenuMeta {
  icon?: ComponentType<OpMenuIconProps>
  label?: string
  create: OpMenuCreateConfig
  sourceNodeTypes?: string[]
  sourceToolbars?: string[]
  order?: number
}

export interface OpQuickMeta {
  id: string
  title: string
  sourceNodeTypes: string[]
  order: number
}

export interface OpDataResolveOptions {
  contextPayloads?: NodeContextPayloadLike[]
  groupExecution?: GroupTaskExecutionContext
}

export interface OpDefaultDataCreateArgs extends OpDataResolveOptions {
  nodeData: BaseNodeData
}

export interface OpCreditBuildArgs<TNodeData = BaseNodeData, TOpData = unknown> extends OpDataResolveOptions {
  nodeId: string
  nodeType: string
  nodeData: TNodeData
  opData: TOpData
}

export interface OpEstimatedItemCountArgs<TNodeData = BaseNodeData, TOpData = unknown> extends OpDataResolveOptions {
  nodeId: string
  nodeType: string
  nodeData: TNodeData
  opData: TOpData
  upstreamItemCounts: number[]
}

export interface OpCreditInput {
  key: string
  data: Record<string, unknown>
}

export type OpCreditBuildResult = {
  ok: boolean
  input?: OpCreditInput | OpCreditInput[] | null
  itemCount?: number
  submitDisabled?: boolean
  disabledMessage?: string
  errorMessage?: string
  errorMessageType?: 'error' | 'warn'
  enforceInBatch?: boolean
}

export interface OpDisplayContext {
  opData: any
  workDatas: any
  submitParams: any
}

export interface OpDefinition {
  op: string
  label: string
  disabled?: boolean
  targetNodeTypes: string[]
  acceptSourceTypes?: string[] | ((targetType: string) => string[])
  maxSourceConnections?: number | ((targetType: string) => number)
  allowMixedExclusiveInputGroup?: boolean
  /** 
   * 提供针对连线被挤占的自定义移除逻辑。
   * 当新增加一条连入线时，如果有特殊数量限制（例如按节点类型区分限制），可以在这里计算出应当被移除的旧连线。
   */
  getEdgesToReplace?: (params: {
    targetNodeType: string
    targetNodeId: string
    incomingSourceNodeId?: string
    incomingSourceNodeType?: string
    currentIncomingEdges: any[]
    nodes: any[] | Record<string, any>
  }) => any[]
  isToolOp?: boolean
  preferenceKeys?: string[]
  menu?: OpMenuMeta | OpMenuMeta[]
  quick?: OpQuickMeta | OpQuickMeta[]
  createDefaultData?: (args: OpDefaultDataCreateArgs) => object
  buildCreditInput?: (args: OpCreditBuildArgs<any, any>) => OpCreditBuildResult
  resolveEstimatedItemCount?: (args: OpEstimatedItemCountArgs<any, any>) => number
  resolveTargetDisplayHint?: (args: {
    nodeType: string
    nodeData: BaseNodeData
    opData: unknown
  }) => OpTargetDisplayHint | undefined

  /** UI 展示相关配置（任务列表、参数弹窗等） */
  display?: {
    hideParams?: ('model' | 'resolution')[]
    resolveLabelSuffix?: (ctx: OpDisplayContext) => string | undefined
    resolveParams?: (ctx: OpDisplayContext) => { label: string; value: string }[]
    resolvePrompt?: (ctx: OpDisplayContext & { entryPrompt: string }) => string | null
  }

  Panel?: ComponentType<NodeToolbarHostComponentProps>
}

type RegisterOpInput = Omit<OpDefinition, 'Panel'>

const opRegistry = new Map<string, OpDefinition>()

export const MATERIAL_OP = 'material'

// 注册一个完整 OP 定义，供自动导入的 OP 模块写入全局注册表。
export function registerOpDefinition(definition: OpDefinition) {
  if (!definition.disabled) {
    opRegistry.set(definition.op, definition)
  }
}

// 包装 OP 面板组件，并把组件和元数据一起注册到 OP 注册表。
export function registerOp(definition: RegisterOpInput) {
  return function <T extends ComponentType<NodeToolbarHostComponentProps>>(Panel: T): T {
    registerOpDefinition({
      ...definition,
      Panel,
    })
    return Panel
  }
}

// 根据 OP 标识读取注册定义。
export function getOp(op: string): OpDefinition | undefined {
  return opRegistry.get(op)
}

// 获取当前已经注册的全部 OP 定义。
export function getAllOps(): OpDefinition[] {
  return Array.from(opRegistry.values())
}

export function resolveOpPreferenceKeys(op: string): readonly string[] {
  return getOp(op)?.preferenceKeys ?? []
}

// 按目标节点类型筛选可渲染的 OP。
export function getOpsByNodeType(nodeType: string): OpDefinition[] {
  return getAllOps().filter((definition) =>
    definition.targetNodeTypes.includes(nodeType),
  )
}

// 解析 OP 接受的上游输入类型。
export function resolveOpAcceptSourceTypes(definition: OpDefinition, targetNodeType: string) {
  return typeof definition.acceptSourceTypes === 'function'
    ? definition.acceptSourceTypes(targetNodeType)
    : definition.acceptSourceTypes
}

// 解析 OP 允许的最大上游输入连接数。
export function resolveOpMaxSourceConnections(definition: OpDefinition, targetNodeType: string) {
  return typeof definition.maxSourceConnections === 'function'
    ? definition.maxSourceConnections(targetNodeType)
    : (definition.maxSourceConnections ?? Infinity)
}

const LIST_NODE_TYPES = new Set(['gallery', 'text-list', 'video-list', 'audio-list'])

/** 
 * 为标准的非工具型 OP 生成基础的连线规则。
 * 默认不限制最大连接数（Infinity）。
 * 如果目标节点是单数节点，自动拒绝来自列表节点的连接；如果目标是列表节点，允许所有配置的连接。
 */
export function createStandardOpConnectionRules(acceptTypes: string[]) {
  return {
    acceptSourceTypes: (targetType: string) => {
      if (!LIST_NODE_TYPES.has(targetType)) {
        return acceptTypes.filter((type) => !LIST_NODE_TYPES.has(type))
      }
      return acceptTypes
    },
    maxSourceConnections: Infinity,
  }
}

/** 
 * 为标准的工具型 OP 生成基于单复数类型的连线规则。
 * 支持传入多组 (单数类型, 列表类型) 组合，使其完全通用化。
 * 对于不支持批量输出的节点（如仅支持单数的裁剪），可以直接传入字符串（如 'image'）。
 * 例如：['text', 'text-list'], 'image'
 */
export function createStandardToolOpConnectionRules(...pairs: ([string, string] | string)[]) {
  const singleToList = new Map<string, string>();
  const listToSingle = new Map<string, string>();

  for (const pair of pairs) {
    if (Array.isArray(pair) && pair.length === 2) {
      const [single, list] = pair;
      singleToList.set(single, list);
      listToSingle.set(list, single);
    }
  }

  return {
    acceptSourceTypes: (targetType: string) => {
      // 目标为列表节点时，允许接收自己及其对应的单数类型输入
      if (listToSingle.has(targetType)) {
        return [targetType, listToSingle.get(targetType)!];
      }
      // 目标为单数节点时，只允许接收自己的单数类型输入
      if (singleToList.has(targetType)) {
        return [targetType];
      }
      // 如果目标类型不在配置对中，默认仅接收同类型
      return [targetType];
    },
    maxSourceConnections: (targetType: string) => {
      // 目标为列表节点时，支持无上限批量输入
      if (listToSingle.has(targetType)) {
        return Infinity;
      }
      // 其他情况（包含单数节点）只允许 1 个输入
      return 1;
    }
  }
}

// 判断值是否为普通对象，避免把数组或空值当作 OP 参数合并。
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// 合并默认 OP 参数和节点已保存参数，已保存字段优先。
function mergeWithDefaults(defaults: unknown, stored: unknown): unknown {
  if (!isRecord(defaults) || !isRecord(stored)) {
    return stored ?? defaults
  }

  const merged: Record<string, unknown> = { ...defaults }
  Object.entries(stored).forEach(([key, value]) => {
    merged[key] = value === undefined ? undefined : mergeWithDefaults(defaults[key], value)
  })

  return merged
}

// 从节点数据中读取指定 OP 已经保存的参数对象。
function getStoredOpData(nodeData: BaseNodeData, op: string) {
  const opData = nodeData.op_data
  const stored = isRecord(opData) ? opData[op] : undefined
  return isRecord(stored) ? stored : undefined
}

// 为节点当前 OP 创建默认参数，供 UI 和 payload 构造使用。
export function createDefaultOpDataForNode<T extends object = Record<string, unknown>>(
  nodeData: BaseNodeData,
  options: OpDataResolveOptions = {},
): T {
  const op = typeof nodeData.op === 'string' ? nodeData.op : ''
  if (!op) {
    return {} as T
  }

  const definition = getOp(op)
  return (definition?.createDefaultData?.({ nodeData, ...options }) ?? {}) as T
}

export function createInitialOpDataForNewNode<T extends object = Record<string, unknown>>(
  nodeData: BaseNodeData,
  options: OpDataResolveOptions = {},
): T {
  const op = typeof nodeData.op === 'string' ? nodeData.op : ''
  if (!op) {
    return {} as T
  }

  const defaults = createDefaultOpDataForNode<T>(nodeData, options)
  const preference = readOpPreference(op, resolveOpPreferenceKeys(op))
  return mergeWithDefaults(defaults, preference) as T
}

// 读取节点当前 OP 参数，并把默认值与已保存字段合并成完整结果。
export function resolveNodeOpData<T extends object = Record<string, unknown>>(
  nodeData: BaseNodeData,
  options: OpDataResolveOptions = {},
): T {
  const op = typeof nodeData.op === 'string' ? nodeData.op : ''
  if (!op) {
    return {} as T
  }

  const defaults = createDefaultOpDataForNode<T>(nodeData, options)
  const stored = getStoredOpData(nodeData, op)
  return mergeWithDefaults(defaults, stored) as T
}

// 解析 OP 对目标节点尺寸的显示建议。
export function resolveOpTargetDisplayHint({
  nodeType,
  nodeData,
}: {
  nodeType: string
  nodeData: BaseNodeData
}): OpTargetDisplayHint | undefined {
  const op = typeof nodeData.op === 'string' ? nodeData.op : ''
  if (!op) {
    return undefined
  }

  const definition = getOp(op)
  if (!definition?.resolveTargetDisplayHint || !definition.targetNodeTypes.includes(nodeType)) {
    return undefined
  }

  const opData = resolveNodeOpData(nodeData)
  return definition.resolveTargetDisplayHint({
    nodeType,
    nodeData,
    opData,
  })
}

export function buildCreditInputForNode({
  nodeId,
  nodeType,
  nodeData,
  contextPayloads,
  groupExecution,
}: {
  nodeId: string
  nodeType: string
  nodeData: BaseNodeData
  contextPayloads?: NodeContextPayloadLike[]
  groupExecution?: GroupTaskExecutionContext
}): OpCreditBuildResult {
  const op = typeof nodeData.op === 'string' ? nodeData.op : ''
  if (!op) {
    return { ok: false, disabledMessage: '当前节点缺少 OP 配置' }
  }

  const definition = getOp(op)
  if (!definition) {
    return { ok: false, disabledMessage: '当前节点 OP 未注册' }
  }

  if (!definition.targetNodeTypes.includes(nodeType)) {
    return { ok: false, disabledMessage: '当前节点类型不支持该 OP' }
  }

  if (!definition.buildCreditInput) {
    return { ok: false, disabledMessage: '当前 OP 缺少提交参数配置' }
  }

  const options = { contextPayloads, groupExecution }
  return definition.buildCreditInput({
    nodeId,
    nodeType,
    nodeData,
    opData: resolveNodeOpData(nodeData, options),
    ...options,
  })
}

export function resolveEstimatedItemCountForNode({
  nodeId,
  nodeType,
  nodeData,
  contextPayloads,
  groupExecution,
  upstreamItemCounts,
}: {
  nodeId: string
  nodeType: string
  nodeData: BaseNodeData
  contextPayloads?: NodeContextPayloadLike[]
  groupExecution?: GroupTaskExecutionContext
  upstreamItemCounts: number[]
}): number {
  const op = typeof nodeData.op === 'string' ? nodeData.op : ''
  const definition = op ? getOp(op) : undefined
  const normalizedUpstreamCounts = upstreamItemCounts
    .filter((count) => Number.isFinite(count) && count > 0)

  if (!definition?.targetNodeTypes.includes(nodeType)) {
    return Math.max(1, ...normalizedUpstreamCounts)
  }

  const options = { contextPayloads, groupExecution }
  const opData = resolveNodeOpData(nodeData, options)
  const resolvedCount = definition.resolveEstimatedItemCount?.({
    nodeId,
    nodeType,
    nodeData,
    opData,
    upstreamItemCounts: normalizedUpstreamCounts,
    ...options,
  })

  if (typeof resolvedCount === 'number' && Number.isFinite(resolvedCount) && resolvedCount > 0) {
    return Math.max(1, Math.ceil(resolvedCount))
  }

  if (normalizedUpstreamCounts.length > 0 && LIST_NODE_TYPES.has(nodeType)) {
    return Math.max(1, ...normalizedUpstreamCounts)
  }

  if (definition.buildCreditInput) {
    const creditInput = definition.buildCreditInput({
      nodeId,
      nodeType,
      nodeData,
      opData,
      ...options,
    })
    if (creditInput.ok && Number.isFinite(creditInput.itemCount) && creditInput.itemCount! > 0) {
      return Math.max(1, Math.ceil(creditInput.itemCount!))
    }
  }

  return 1
}
